import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Badge,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
  SimpleGrid,
  Flex,
  Image,
  InputGroup,
  InputRightElement,
  IconButton,
  Link,
} from '@chakra-ui/react';

// ========== Helpers ==========

const API_HEADERS = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const statusColor = (s) => {
  if (s === 'ready' || s === 'connected') return 'green';
  if (s === 'qr_pending' || s === 'initializing' || s === 'reconnecting') return 'yellow';
  if (s === 'unavailable') return 'gray';
  return 'red';
};

const StatusBadge = ({ status, label }) => (
  <Badge colorScheme={statusColor(status)} borderRadius="full" px={3}>
    {label || status}
  </Badge>
);

// ========== Telegram Section ==========

function TelegramSection() {
  const toast = useToast();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [botToken, setBotToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [newChatId, setNewChatId] = useState('');
  const [chatIds, setChatIds] = useState([]);
  const [testChatId, setTestChatId] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/telegram/config', { headers: API_HEADERS() });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setChatIds(data.chatIds || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (!botToken.trim()) {
      toast({ title: 'Token obrigatório', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/notifications/telegram/config', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({ botToken: botToken.trim(), chatIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      toast({ title: 'Telegram configurado!', description: `Bot: @${data.botInfo?.username}`, status: 'success', duration: 4000, isClosable: true });
      setBotToken('');
      await fetchConfig();
    } catch (e) {
      toast({ title: 'Erro', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleAddChatId = async () => {
    const id = newChatId.trim();
    if (!id || chatIds.includes(id)) return;
    const updated = [...chatIds, id];
    setChatIds(updated);
    setNewChatId('');
    if (config?.configured) {
      await saveChatIds(updated);
    }
  };

  const handleRemoveChatId = async (id) => {
    const updated = chatIds.filter((c) => c !== id);
    setChatIds(updated);
    if (config?.configured) {
      await saveChatIds(updated);
    }
  };

  const saveChatIds = async (ids) => {
    try {
      const res = await fetch('/api/notifications/telegram/chats', {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({ chatIds: ids }),
      });
      if (res.ok) {
        toast({ title: 'Chat IDs atualizados', status: 'success', duration: 2000, isClosable: true });
      }
    } catch (e) {
      toast({ title: 'Erro ao salvar chat IDs', status: 'error', duration: 2000, isClosable: true });
    }
  };

  const handleToggle = async () => {
    if (!config?.configured) return;
    setToggling(true);
    try {
      const res = await fetch('/api/notifications/telegram/toggle', {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({ enabled: !config.isEnabled }),
      });
      if (res.ok) {
        await fetchConfig();
        toast({ title: config.isEnabled ? 'Telegram desabilitado' : 'Telegram habilitado', status: 'info', duration: 2000, isClosable: true });
      }
    } catch (e) {
      toast({ title: 'Erro', description: e.message, status: 'error', duration: 2000, isClosable: true });
    } finally {
      setToggling(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch('/api/notifications/telegram/test', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({ chatId: testChatId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro no teste');
      toast({ title: 'Mensagem de teste enviada!', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      toast({ title: 'Erro no teste', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Flex justify="center" py={6}><Spinner size="md" /></Flex>;

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center">
        <HStack spacing={3}>
          <Heading size="md">Telegram</Heading>
          {config?.configured ? (
            <StatusBadge status={config.isEnabled ? 'ready' : 'disconnected'} label={config.isEnabled ? 'Ativo' : 'Desabilitado'} />
          ) : (
            <StatusBadge status="disconnected" label="Não configurado" />
          )}
          {config?.botInfo && (
            <Link href={`https://t.me/${config.botInfo.username}`} isExternal fontSize="sm" color="teal.500" fontWeight="medium">
              @{config.botInfo.username} ↗
            </Link>
          )}
        </HStack>
        {config?.configured && (
          <HStack>
            <Text fontSize="sm" color="gray.600">{config.isEnabled ? 'Broadcast habilitado' : 'Broadcast desabilitado'}</Text>
            <Switch colorScheme="teal" isChecked={!!config.isEnabled} onChange={handleToggle} isDisabled={toggling} />
          </HStack>
        )}
      </Flex>

      {config?.configured && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">Token: {config.maskedToken} · {chatIds.length} destino(s) de broadcast</Text>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <FormControl>
          <FormLabel fontSize="sm">Token do Bot</FormLabel>
          <InputGroup>
            <Input
              size="sm"
              type={showToken ? 'text' : 'password'}
              placeholder={config?.configured ? 'Novo token (deixe vazio para manter)' : 'Cole o token do BotFather'}
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
            />
            <InputRightElement h="32px">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={showToken ? 'Ocultar' : 'Mostrar'}
                icon={<Text fontSize="xs">{showToken ? '🙈' : '👁'}</Text>}
                onClick={() => setShowToken(!showToken)}
              />
            </InputRightElement>
          </InputGroup>
          <FormHelperText fontSize="xs">Crie um bot via @BotFather no Telegram.</FormHelperText>
          <Button mt={3} size="sm" colorScheme="teal" onClick={handleSave} isLoading={saving} loadingText="Validando..." isDisabled={!botToken.trim()}>
            {config?.configured ? 'Atualizar Bot' : 'Configurar Bot'}
          </Button>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Broadcast Global — Chat IDs <Text as="span" fontSize="xs" fontWeight="normal" color="gray.400">(opcional)</Text></FormLabel>
          <HStack>
            <Input
              size="sm"
              placeholder="Ex: -1001234567890"
              value={newChatId}
              onChange={(e) => setNewChatId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddChatId()}
            />
            <Button size="sm" colorScheme="teal" variant="outline" onClick={handleAddChatId} isDisabled={!newChatId.trim()}>
              Adicionar
            </Button>
          </HStack>
          <FormHelperText fontSize="xs">
            Grupos ou chats que recebem <strong>todos</strong> os alertas. Use @getidsbot para descobrir o ID.{' '}
            Para notificar usuários específicos por placa, configure o Telegram Chat ID no cadastro do usuário.
          </FormHelperText>
          <Flex wrap="wrap" gap={2} mt={3}>
            {chatIds.length === 0
              ? <Text fontSize="sm" color="gray.400">Nenhum broadcast global configurado</Text>
              : chatIds.map((id) => (
                <Tag key={id} colorScheme="teal" size="sm">
                  <TagLabel>{id}</TagLabel>
                  <TagCloseButton onClick={() => handleRemoveChatId(id)} />
                </Tag>
              ))
            }
          </Flex>
        </FormControl>
      </SimpleGrid>

      {config?.configured && config?.isEnabled && (
        <>
          <Divider />
          {config?.botInfo && (
            <Alert status="warning" borderRadius="md" py={2}>
              <AlertIcon />
              <Text fontSize="xs">
                Antes de receber mensagens, você (ou qualquer destinatário) precisa enviar{' '}
                <Text as="span" fontWeight="bold">/start</Text> para o bot.{' '}
                <Link href={`https://t.me/${config.botInfo.username}`} isExternal color="blue.500" fontWeight="semibold">
                  Abrir @{config.botInfo.username} no Telegram ↗
                </Link>
              </Text>
            </Alert>
          )}
          <HStack>
            <Input size="sm" placeholder="Chat ID para teste (opcional)" value={testChatId} onChange={(e) => setTestChatId(e.target.value)} maxW="xs" />
            <Button size="sm" colorScheme="teal" variant="outline" onClick={handleTest} isLoading={testing} loadingText="Enviando...">
              Enviar Teste
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
}

// ========== WhatsApp Section ==========

function WhatsAppSection() {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);

  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [recipientNumbers, setRecipientNumbers] = useState([]);
  const [newNumber, setNewNumber] = useState('');
  const [testNumber, setTestNumber] = useState('');

  const pollingRef = useRef(null);
  const statusPollingRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/whatsapp/config', { headers: API_HEADERS() });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setRecipientNumbers(data.recipientNumbers || []);
        return data;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling de status quando desconectado ou reconectando (detecta auto-reconexão do backend)
  const stopStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  };

  const startStatusPolling = useCallback(() => {
    stopStatusPolling();
    statusPollingRef.current = setInterval(async () => {
      try {
        const res = await fetch('/api/notifications/whatsapp/config', { headers: API_HEADERS() });
        if (res.ok) {
          const data = await res.json();
          setStatus(data);
          setRecipientNumbers(data.recipientNumbers || []);
          if (data.isReady) {
            stopStatusPolling();
            stopPolling();
            setQrImageUrl(null);
            toast({ title: 'WhatsApp reconectado!', status: 'success', duration: 4000, isClosable: true });
          }
        }
      } catch (e) {}
    }, 5000);
  }, [toast]);

  useEffect(() => {
    fetchStatus().then((data) => {
      if (data && !data.isReady && data.available !== false) {
        startStatusPolling();
      }
    });
    return () => { stopPolling(); stopStatusPolling(); };
  }, [fetchStatus, startStatusPolling]);

  // Iniciar/parar polling de status conforme o estado muda
  useEffect(() => {
    if (!status) return;
    if (!status.isReady && status.available !== false) {
      startStatusPolling();
    } else {
      stopStatusPolling();
    }
  }, [status?.isReady, status?.available, startStatusPolling]);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startQrPolling = () => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch('/api/notifications/whatsapp/qrcode', { headers: API_HEADERS() });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'ready') {
            stopPolling();
            setQrImageUrl(null);
            await fetchStatus();
            toast({ title: 'WhatsApp conectado!', status: 'success', duration: 4000, isClosable: true });
          } else if (data.qrImageUrl) {
            setQrImageUrl(data.qrImageUrl);
          }
        }
      } catch (e) {}
    }, 3000);
  };

  const handleConnect = async () => {
    setConnecting(true);
    setQrImageUrl(null);
    try {
      const res = await fetch('/api/notifications/whatsapp/connect', {
        method: 'POST',
        headers: API_HEADERS(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao conectar');

      if (data.status === 'ready') {
        toast({ title: 'WhatsApp já está conectado!', status: 'success', duration: 3000, isClosable: true });
        await fetchStatus();
      } else if (data.qrImageUrl) {
        setQrImageUrl(data.qrImageUrl);
        startQrPolling();
      } else {
        // Inicialização em andamento, começar polling
        startQrPolling();
        toast({ title: 'Aguardando QR Code...', description: 'O QR Code aparecerá em instantes', status: 'info', duration: 3000, isClosable: true });
      }
    } catch (e) {
      toast({ title: 'Erro ao conectar', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    stopPolling();
    setQrImageUrl(null);
    try {
      const res = await fetch('/api/notifications/whatsapp/disconnect', {
        method: 'POST',
        headers: API_HEADERS(),
      });
      if (res.ok) {
        toast({ title: 'WhatsApp desconectado', status: 'info', duration: 3000, isClosable: true });
        await fetchStatus();
      }
    } catch (e) {
      toast({ title: 'Erro', description: e.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await fetch('/api/notifications/whatsapp/toggle', {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({ enabled: !status?.isEnabled }),
      });
      if (res.ok) {
        await fetchStatus();
        toast({ title: status?.isEnabled ? 'WhatsApp desabilitado' : 'WhatsApp habilitado', status: 'info', duration: 2000, isClosable: true });
      }
    } catch (e) {
      toast({ title: 'Erro', description: e.message, status: 'error', duration: 2000, isClosable: true });
    } finally {
      setToggling(false);
    }
  };

  const handleSaveNumbers = async () => {
    if (recipientNumbers.length === 0) {
      toast({ title: 'Adicione pelo menos um número', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/notifications/whatsapp/config', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({ recipientNumbers }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      toast({ title: 'Números salvos!', status: 'success', duration: 2000, isClosable: true });
      await fetchStatus();
    } catch (e) {
      toast({ title: 'Erro', description: e.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNumber = () => {
    const n = newNumber.trim().replace(/\D/g, '');
    if (!n || recipientNumbers.includes(n)) return;
    setRecipientNumbers([...recipientNumbers, n]);
    setNewNumber('');
  };

  const handleRemoveNumber = (n) => setRecipientNumbers(recipientNumbers.filter((x) => x !== n));

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch('/api/notifications/whatsapp/test', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({ number: testNumber || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro no teste');
      toast({ title: 'Mensagem enviada!', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      toast({ title: 'Erro no teste', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Flex justify="center" py={6}><Spinner size="md" /></Flex>;

  const isConnected = status?.isReady;
  const isUnavailable = status?.available === false;

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center">
        <HStack spacing={3}>
          <Heading size="md">WhatsApp</Heading>
          <StatusBadge
            status={isUnavailable ? 'unavailable' : isConnected ? 'ready' : status?.status || 'disconnected'}
            label={
              isUnavailable ? 'Indisponível'
              : isConnected ? 'Conectado'
              : status?.status === 'initializing' ? 'Iniciando...'
              : status?.status === 'qr_pending' ? 'Aguardando QR'
              : status?.status === 'reconnecting' ? `Reconectando (${status.reconnectAttempts}/${status.maxReconnectAttempts})...`
              : 'Desconectado'
            }
          />
        </HStack>
        {!isUnavailable && isConnected && (
          <HStack>
            <Text fontSize="sm" color="gray.600">{status?.isEnabled ? 'Broadcast habilitado' : 'Broadcast desabilitado'}</Text>
            <Switch colorScheme="teal" isChecked={!!status?.isEnabled} onChange={handleToggle} isDisabled={toggling} />
          </HStack>
        )}
      </Flex>

      {isUnavailable && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Biblioteca não instalada</AlertTitle>
            <AlertDescription fontSize="xs">
              Execute no servidor: <code>npm install whatsapp-web.js qrcode</code>
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {status?.status === 'reconnecting' && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Reconectando automaticamente…</AlertTitle>
            <AlertDescription fontSize="xs">
              O WhatsApp foi desconectado inesperadamente. Tentativa {status.reconnectAttempts} de {status.maxReconnectAttempts}.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">Conexão</Text>

          {!isUnavailable && !isConnected && !qrImageUrl && (
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" color="gray.500">Conecte seu número escaneando o QR Code com o WhatsApp.</Text>
              <Button size="sm" colorScheme="teal" onClick={handleConnect} isLoading={connecting} loadingText="Iniciando...">
                Conectar WhatsApp
              </Button>
            </VStack>
          )}

          {!isConnected && qrImageUrl && (
            <VStack align="center" spacing={2}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">Escaneie o QR Code com seu WhatsApp</Text>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Abra o WhatsApp → Dispositivos vinculados → Vincular dispositivo
              </Text>
              <Box border="2px solid" borderColor="teal.400" borderRadius="md" p={2} bg="white" boxShadow="md">
                <Image src={qrImageUrl} alt="QR Code WhatsApp" w="220px" h="220px" />
              </Box>
              <HStack>
                <Spinner size="xs" color="teal.500" />
                <Text fontSize="xs" color="gray.500">Aguardando escaneamento...</Text>
              </HStack>
              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => { stopPolling(); setQrImageUrl(null); }}>
                Cancelar
              </Button>
            </VStack>
          )}

          {isConnected && (
            <VStack align="start" spacing={3}>
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">Conectado e pronto para enviar mensagens.</Text>
              </Alert>
              <Button size="sm" colorScheme="red" variant="outline" onClick={handleDisconnect} isLoading={disconnecting} loadingText="Desconectando...">
                Desconectar
              </Button>
            </VStack>
          )}
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={1} color="gray.700">
            Broadcast Global — Números de destino{' '}
            <Text as="span" fontSize="xs" fontWeight="normal" color="gray.400">(opcional)</Text>
          </Text>
          <Text fontSize="xs" color="gray.500" mb={3}>
            Recebem <strong>todos</strong> os alertas. Formato sem '+': <strong>5511999998888</strong>.<br />
            Para notificar usuários específicos por placa, configure o telefone no cadastro do usuário.
          </Text>
          <HStack mb={3}>
            <Input size="sm" placeholder="Ex: 5511999998888" value={newNumber} onChange={(e) => setNewNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddNumber()} type="tel" />
            <Button size="sm" colorScheme="teal" variant="outline" onClick={handleAddNumber} isDisabled={!newNumber.trim()}>Adicionar</Button>
          </HStack>
          <Flex wrap="wrap" gap={2} mb={3}>
            {recipientNumbers.length === 0
              ? <Text fontSize="sm" color="gray.400">Nenhum broadcast global configurado</Text>
              : recipientNumbers.map((n) => (
                <Tag key={n} colorScheme="teal" size="sm">
                  <TagLabel>+{n}</TagLabel>
                  <TagCloseButton onClick={() => handleRemoveNumber(n)} />
                </Tag>
              ))
            }
          </Flex>
          <Button size="sm" colorScheme="teal" onClick={handleSaveNumbers} isLoading={saving} isDisabled={recipientNumbers.length === 0}>
            Salvar Números
          </Button>
        </Box>
      </SimpleGrid>

      {!isUnavailable && (
        <>
          <Divider />
          <HStack>
            <Input size="sm" placeholder="Número para teste (opcional)" value={testNumber} onChange={(e) => setTestNumber(e.target.value)} maxW="xs" type="tel" />
            <Button
              size="sm"
              colorScheme="teal"
              variant="outline"
              onClick={handleTest}
              isLoading={testing}
              loadingText="Enviando..."
              isDisabled={!isConnected}
            >
              Enviar Teste
            </Button>
          </HStack>
          {!isConnected && (
            <Text fontSize="xs" color="gray.400">Conecte o WhatsApp para enviar mensagens de teste.</Text>
          )}
        </>
      )}
    </VStack>
  );
}

// ========== Main Export ==========

export default function NotificationSettingsTab() {
  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box fontSize="sm">
          <AlertTitle mb={1}>Como funcionam as notificações</AlertTitle>
          <AlertDescription>
            Ao detectar uma placa monitorada, o sistema notifica automaticamente:
          </AlertDescription>
          <Box as="ul" pl={4} mt={1} fontSize="xs" lineHeight="tall">
            <li><strong>Usuários interessados</strong>: cada placa monitorada pode ter usuários vinculados. Eles recebem a notificação no seu Telegram/WhatsApp pessoal (configurado no cadastro de cada usuário).</li>
            <li><strong>Broadcast global</strong> (opcional): Chat IDs ou números cadastrados aqui recebem <em>todos</em> os alertas, independente da placa ou usuário.</li>
          </Box>
        </Box>
      </Alert>

      <Box bg="white" boxShadow="md" borderRadius="md" p={6}>
        <TelegramSection />
      </Box>

      <Box bg="white" boxShadow="md" borderRadius="md" p={6}>
        <WhatsAppSection />
      </Box>
    </VStack>
  );
}
